import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { Session } from '@claude-code-api/shared';
import { Badge, Button, Card, CardContent, Spinner } from '@/components/ui';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { EmptyState } from '@/components/shared/EmptyState';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { StartSessionModal } from '@/components/StartSessionModal';
import { getProjectWorkflowUrl, isTeamWorkflowRunner } from '@/lib/gift-agent';
import { toast } from 'sonner';
import { Play, Cpu, HardDrive, Square, RotateCcw } from 'lucide-react';

interface ProjectSessionsTabProps {
  projectId: string;
  isTeamMode?: boolean;
}

export function ProjectSessionsTab({
  projectId,
  isTeamMode = false,
}: ProjectSessionsTabProps) {
  const { t } = useTranslation(['projects', 'common']);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [sessionModalProjectId, setSessionModalProjectId] = useState<
    string | null
  >(null);
  const [sessionToStop, setSessionToStop] = useState<string | null>(null);
  const [stoppingSession, setStoppingSession] = useState(false);
  const [sessionToRestart, setSessionToRestart] = useState<string | null>(null);
  const [restartingSession, setRestartingSession] = useState(false);

  useEffect(() => {
    fetchSessions();
  }, [projectId]);

  const fetchSessions = async () => {
    try {
      const response = await fetch(`/api/sessions?projectId=${projectId}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      toast.error(t('projects:sessions.errors.load'));
    } finally {
      setLoading(false);
    }
  };

  const stopSession = async () => {
    if (!sessionToStop) return;
    setStoppingSession(true);
    try {
      const response = await fetch(`/api/sessions/${sessionToStop}/stop`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success(t('projects:sessions.messages.stopped'));
        fetchSessions();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.error || t('projects:sessions.errors.stop'));
      }
    } catch (error) {
      console.error('Failed to stop session:', error);
      toast.error(t('projects:sessions.errors.stop'));
    } finally {
      setStoppingSession(false);
      setSessionToStop(null);
    }
  };

  const restartSession = async () => {
    if (!sessionToRestart) return;
    setRestartingSession(true);
    try {
      const response = await fetch(
        `/api/sessions/${sessionToRestart}/restart`,
        {
          method: 'POST',
          credentials: 'include',
        },
      );
      if (response.ok) {
        toast.success(t('projects:sessions.messages.restarted'));
        fetchSessions();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.error || t('projects:sessions.errors.restart'));
      }
    } catch (error) {
      console.error('Failed to restart session:', error);
      toast.error(t('projects:sessions.errors.restart'));
    } finally {
      setRestartingSession(false);
      setSessionToRestart(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <EmptyState
        icon={Play}
        title={t('projects:sessions.emptyTitle')}
        description={
          isTeamMode
            ? 'The Team AI coding session is created automatically for team-mode projects.'
            : t('projects:sessions.emptyDescription')
        }
        action={
          isTeamMode ? undefined : (
            <Button onClick={() => setSessionModalProjectId(projectId)}>
              <Play className="w-4 h-4 mr-2" />
              {t('projects:sessions.start')}
            </Button>
          )
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {t('projects:sessions.count', { count: sessions.length })}
        </p>
        {!isTeamMode ? (
          <Button onClick={() => setSessionModalProjectId(projectId)} size="sm">
            <Play className="w-4 h-4 mr-2" />
            {t('projects:sessions.start')}
          </Button>
        ) : null}
      </div>

      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="divide-y">
            {sessions.map((session) => (
              <div
                key={session.id}
                className="px-6 py-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      session.status === 'running'
                        ? 'bg-success/15'
                        : session.status === 'pending'
                          ? 'bg-warning/15'
                          : 'bg-muted'
                    }`}
                  >
                    <Play
                      className={`w-5 h-5 ${
                        session.status === 'running'
                          ? 'text-success'
                          : session.status === 'pending'
                            ? 'text-warning'
                            : 'text-muted-foreground'
                      }`}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Link
                        to={
                          isTeamWorkflowRunner(session.runnerType)
                            ? getProjectWorkflowUrl(projectId)
                            : `/sessions/${session.id}`
                        }
                        className="text-sm font-medium hover:underline"
                      >
                        {isTeamWorkflowRunner(session.runnerType)
                          ? t('projects:sessions.openWorkflow')
                          : t('projects:sessions.sessionLabel', {
                              id: session.id.slice(0, 8),
                            })}
                      </Link>
                      <Badge
                        variant={
                          session.status === 'running'
                            ? 'running'
                            : session.status === 'pending'
                              ? 'pending'
                              : 'stopped'
                        }
                        className="gap-1"
                      >
                        <StatusIndicator
                          status={
                            session.status === 'running'
                              ? 'running'
                              : session.status === 'pending'
                                ? 'pending'
                                : 'stopped'
                          }
                          size="sm"
                        />
                        {session.status}
                      </Badge>
                      {session.runnerType && (
                        <Badge variant="secondary" className="text-xs">
                          {session.runnerType}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Cpu className="w-3 h-3 text-primary" />{' '}
                        {t('projects:sessions.cpu', {
                          value: session.cpuCores,
                        })}
                      </span>
                      <span className="flex items-center gap-1">
                        <HardDrive className="w-3 h-3 text-accent" />{' '}
                        {t('projects:sessions.memory', {
                          value: session.memoryGb,
                        })}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {session.status === 'running' && (
                    <>
                      <Button
                        asChild
                        variant="ghost"
                        size="sm"
                        className="text-primary hover:text-primary/80"
                      >
                        <Link to={`/sessions/${session.id}`}>
                          {t('projects:sessions.open')}
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSessionToStop(session.id)}
                        className="text-destructive hover:text-destructive/80"
                      >
                        <Square className="w-3 h-3 mr-1" />
                        {t('projects:sessions.stop')}
                      </Button>
                    </>
                  )}
                  {session.status === 'stopped' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSessionToRestart(session.id)}
                      className="text-primary hover:text-primary/80"
                    >
                      <RotateCcw className="w-3 h-3 mr-1" />
                      {t('projects:sessions.restart')}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {!isTeamMode ? (
        <StartSessionModal
          projectId={sessionModalProjectId}
          onClose={() => setSessionModalProjectId(null)}
          onSessionCreated={() => fetchSessions()}
        />
      ) : null}

      <ConfirmDialog
        open={sessionToStop !== null}
        onOpenChange={(open) => {
          if (!open) setSessionToStop(null);
        }}
        title={t('projects:sessions.stopTitle')}
        description={t('projects:sessions.stopDescription')}
        confirmLabel={t('projects:sessions.stop')}
        cancelLabel={t('common:cancel')}
        onConfirm={stopSession}
        loading={stoppingSession}
        variant="destructive"
      />

      <ConfirmDialog
        open={sessionToRestart !== null}
        onOpenChange={(open) => {
          if (!open) setSessionToRestart(null);
        }}
        title={t('projects:sessions.restartTitle')}
        description={t('projects:sessions.restartDescription')}
        confirmLabel={t('projects:sessions.restart')}
        cancelLabel={t('common:cancel')}
        onConfirm={restartSession}
        loading={restartingSession}
        variant="default"
      />
    </div>
  );
}
